import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { AccumulatorEdit } from '../model/accumulatorEdit';
import { AccumulatorValidators } from 'src/app/core/validators/accumulator.validators';
import { Code, Dictionary } from 'src/app/core/model/dictionary';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';

@Component({
  selector: 'app-accumulator-edit',
  templateUrl: './accumulator-edit.component.html',
  styleUrls: ['./accumulator-edit.component.scss']
})

export class AccumulatorEditComponent implements OnInit
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<AccumulatorEdit> = new EventEmitter();

  @ViewChild('maxUnitsControl') public maxUnitsControl: NumericTextBoxComponent;

  public frequencies: Code[] = [];
  public editForm: any;
  public editFormActive = false;

  constructor(
    private fb: UntypedFormBuilder,
    private accumulatorValidators: AccumulatorValidators,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService) { }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get maxUnits(): UntypedFormControl
  {
    return this.editForm.get('maxUnits');
  }

  public get maxAmount(): UntypedFormControl
  {
    return this.editForm.get('maxAmount');
  }

  public get frequency(): UntypedFormControl
  {
    return this.editForm.get('frequency');
  }

  public get duration(): UntypedFormControl
  {
    return this.editForm.get('duration');
  }

  public get enabled(): UntypedFormControl
  {
    return this.editForm.get('enabled');
  }

  public get startDate(): UntypedFormControl 
  {
    return this.editForm.get('startDate');
  }

  public get validateMaximum(): UntypedFormControl
  {
    return this.editForm.get('validateMaximum');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  @Input() public set model(accumulator: AccumulatorEdit)
  {
    this.initModel(accumulator);
  }

  public ngOnInit(): void
  {
    this.dictionaryService.getCachedFrequencies().subscribe({
      next: (data) =>
      {
        this.frequencies = data;
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    // Validations
    if (!this.maxUnits.value && !this.maxAmount.value)
    {
      this.messageHandlerService.displayError('ACCUMULATOR-MAX-UNITS-AMOUNT-REQUIRED');
      this.maxUnitsControl.focus();
      return;
    }

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(accumulatorEdit: AccumulatorEdit)
  {
    if (accumulatorEdit === undefined)
    {
      return;
    }

    // Set the current accumulator id used for name async validators
    const previousAccumulatorName = this.isNew ? '' : accumulatorEdit.name;

    // Set form
    this.editForm = this.fb.group({
      id: [accumulatorEdit.id],
      version: [accumulatorEdit.version],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.accumulatorValidators.validateAccumulatorNameUnique(this.editForm.get('name'), previousAccumulatorName)
        }
      ],
      description: { value: accumulatorEdit.description, disabled: false },
      maxUnits: { value: accumulatorEdit.maxUnits, disabled: false },
      maxAmount: { value: accumulatorEdit.maxAmount, disabled: false },
      frequency: [{ value: accumulatorEdit.frequency, disabled: false }, Validators.required],
      duration: { value: accumulatorEdit.duration, disabled: false },
      enabled: [{ value: accumulatorEdit.enabled, disabled: false }, Validators.required],
      validateMaximum: [{ value: accumulatorEdit.validateMaximum, disabled: false }, Validators.required],
      startDate: [{ value: accumulatorEdit.startDate ? new Date(`${accumulatorEdit.startDate}T12:00:00.000`) : null, disabled: false }, Validators.required]
    });

    this.editForm.get('frequency').valueChanges.subscribe({
      next: (frequency: Dictionary) =>
      {
        this.setDurationControlState(frequency);
      }
    });

    this.name.setValue(accumulatorEdit.name);
    this.setDurationControlState(this.frequency.value);
    this.editFormActive = accumulatorEdit !== undefined;
  }

  private setDurationControlState(frequency: Dictionary): void 
  {
    if (frequency !== null && frequency !== undefined &&
      (frequency.code === 'weekly' || frequency.code === 'monthly' || frequency.code === 'annually'))
    {
      this.duration.enable();
    }
    else
    {
      this.duration.disable();
      this.duration.setValue(null);
    }
    this.duration.updateValueAndValidity();
  }
}
